import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertOctagon,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Download,
  Eye,
  FilePlus2,
  FileText,
  GitCommitHorizontal,
  Loader2,
  Lock,
  RefreshCw,
  Share2,
  Trash2,
} from "lucide-react";
import type { AssuranceAssessment } from "../../../assurance/types";
import {
  createReportRun,
  deleteReportRun,
  fetchReportRuns,
  fetchReportTemplates,
  regenerateReportRun,
  reportDownloadHref,
  reportPreviewHref,
  shareReportRun,
  type ReportRun,
  type ReportTypeInfo,
} from "../../../assurance/data";
import type { AssuranceScanState } from "../../../assurance/model";
import { selectScan } from "../../../assurance/model";
import { asRecord, readString } from "../../../assurance/data";
import {
  CaveatNote,
  EmptyPanel,
  MetricTile,
  Pill,
  SectionPanel,
  fmtDateTime,
  fmtNumber,
} from "../primitives";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function fmtBytes(bytes: number): string {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_META: Record<
  string,
  { label: string; tone: "neutral" | "info" | "success" | "critical" | "warning"; icon: React.ReactNode }
> = {
  queued: { label: "Queued", tone: "neutral", icon: <Clock3 className="h-3 w-3" aria-hidden="true" /> },
  generating: {
    label: "Generating",
    tone: "info",
    icon: <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />,
  },
  completed: {
    label: "Completed",
    tone: "success",
    icon: <CheckCircle2 className="h-3 w-3" aria-hidden="true" />,
  },
  failed: { label: "Failed", tone: "critical", icon: <AlertOctagon className="h-3 w-3" aria-hidden="true" /> },
  expired: { label: "Expired", tone: "warning", icon: <Clock3 className="h-3 w-3" aria-hidden="true" /> },
};

function RunStatusPill({ run }: { run: ReportRun }) {
  const meta = STATUS_META[run.status] || STATUS_META.queued;
  return (
    <Pill tone={meta.tone} icon={meta.icon}>
      {meta.label}
    </Pill>
  );
}

const FORMAT_LABELS: Record<string, string> = {
  pdf: "PDF",
  html: "HTML",
  json: "JSON",
  csv: "CSV",
  cyclonedx: "CycloneDX",
  spdx: "SPDX",
  sarif: "SARIF",
  xml: "XML",
};

// ---------------------------------------------------------------------------
// Generation wizard — six explicit steps
// ---------------------------------------------------------------------------

const WIZARD_STEPS = ["Scope", "Report type", "Content", "Output", "Review", "Generate"];

const CONTENT_TOGGLES: { key: string; label: string; detail: string }[] = [
  { key: "includeExecutiveSummary", label: "Executive summary", detail: "Leadership-ready posture narrative" },
  { key: "includeArchitecture", label: "Architecture evidence", detail: "Evidence-derived component exposure" },
  { key: "includeFindings", label: "Finding inventory", detail: "Complete normalized finding detail" },
  { key: "includeRemediation", label: "Remediation plan", detail: "Evidence-ranked 30/60/90 sequencing" },
  { key: "includeCompliance", label: "Compliance mapping", detail: "Control-relevant evidence, without certification claims" },
  { key: "includeSbom", label: "Supply chain & SBOM", detail: "Dependency and license inventory" },
  { key: "includeAppendices", label: "Appendices", detail: "Integrity, provenance, and evidence boundaries" },
];

function ReportWizard({
  repoId,
  assessment,
  onClose,
  onCreated,
}: {
  repoId: string;
  assessment: AssuranceAssessment;
  onClose: () => void;
  onCreated: (run: ReportRun) => void;
}) {
  const templatesQuery = useQuery({
    queryKey: ["assurance", "report-templates"],
    queryFn: fetchReportTemplates,
    staleTime: 10 * 60 * 1000,
  });
  const [step, setStep] = useState(0);
  const [reportType, setReportType] = useState("comprehensive");
  const [toggles, setToggles] = useState<Record<string, boolean>>(
    Object.fromEntries(CONTENT_TOGGLES.map((toggle) => [toggle.key, true]))
  );
  const [severityThreshold, setSeverityThreshold] = useState("info");
  const [statuses, setStatuses] = useState<string[]>(["open"]);
  const [classification, setClassification] = useState("");
  const [clientName, setClientName] = useState("");
  const [preparedFor, setPreparedFor] = useState("");
  const [formats, setFormats] = useState<string[]>(["pdf", "html"]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const scan = selectScan(assessment);
  const commit = readString(asRecord(assessment.source.scan), ["commit", "commitSha"]);
  const sbomAvailable = Boolean(asRecord(assessment.source.sbom).id);
  const repository = assessment.repository;
  const catalog = templatesQuery.data;
  const selectedType: ReportTypeInfo | undefined = catalog?.reportTypes.find(
    (type) => type.id === reportType
  );

  const toggleFormat = (format: string) => {
    setFormats((current) =>
      current.includes(format)
        ? current.filter((item) => item !== format)
        : [...current, format]
    );
  };
  const toggleStatus = (status: string) => {
    setStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status]
    );
  };

  const canContinue =
    step === 3 ? formats.length > 0 : step === 2 ? statuses.length > 0 : true;

  const submit = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await createReportRun(repoId, {
        reportType,
        formats,
        options: {
          ...toggles,
          severityThreshold,
          statuses,
          ...(classification.trim() ? { classification: classification.trim() } : {}),
          ...(clientName.trim() ? { clientName: clientName.trim() } : {}),
          ...(preparedFor.trim() ? { preparedFor: preparedFor.trim() } : {}),
        },
      });
      if (result.report) onCreated(result.report);
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : "Report generation request failed."
      );
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <SectionPanel
      title="Generate report"
      description="A report is generated asynchronously from an immutable evidence snapshot captured when you submit this request"
      actions={
        <button type="button" onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">
          Cancel
        </button>
      }
    >
      <ol className="mb-5 flex flex-wrap items-center gap-1.5" aria-label="Wizard steps">
        {WIZARD_STEPS.map((label, index) => (
          <li key={label} className="flex items-center gap-1.5">
            <span
              className={
                index === step
                  ? "inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-[11px] font-semibold text-brand ring-1 ring-brand/30"
                  : index < step
                    ? "inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-[11px] font-medium text-success ring-1 ring-success/20"
                    : "inline-flex items-center gap-1.5 rounded-full bg-surface2/60 px-2.5 py-1 text-[11px] font-medium text-muted ring-1 ring-border/60"
              }
            >
              {index < step ? <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> : null}
              {index + 1}. {label}
            </span>
            {index < WIZARD_STEPS.length - 1 && (
              <span className="h-px w-3 bg-border/70" aria-hidden="true" />
            )}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-3">
          <p className="text-[13px] leading-6 text-text2">
            Reports are bound to the latest completed scan of this repository. The evidence
            snapshot is frozen at request time — later scans and triage decisions never change a
            generated report.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile label="Repository" value={repository?.name} />
            <MetricTile
              label="Scan"
              value={scan?.scanId ? String(scan.scanId).slice(0, 8) : undefined}
              sub={scan?.status ? `status ${scan.status}` : undefined}
            />
            <MetricTile
              label="Commit"
              value={
                commit && /^[0-9a-f]{7,64}$/i.test(commit) ? commit.slice(0, 10) : undefined
              }
            />
            <MetricTile
              label="Findings on record"
              value={fmtNumber(assessment.findings.length)}
            />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-2 lg:grid-cols-2">
          {(catalog?.reportTypes || []).map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setReportType(type.id)}
              aria-pressed={type.id === reportType}
              className={
                type.id === reportType
                  ? "rounded-lg border border-brand/50 bg-brand/5 px-4 py-3 text-left ring-1 ring-brand/30 transition-colors"
                  : "rounded-lg border border-border/70 bg-surface2/40 px-4 py-3 text-left transition-colors hover:border-brand/40"
              }
            >
              <span className="block text-[13px] font-semibold text-text">{type.title}</span>
              <span className="mt-0.5 block text-[11.5px] leading-5 text-text2">
                {type.description}
              </span>
              <span className="mt-1 block text-[10.5px] uppercase tracking-wide text-muted">
                {type.audience}
              </span>
            </button>
          ))}
          {templatesQuery.isLoading && (
            <div className="skeleton h-20 rounded-lg" aria-hidden="true" />
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="grid gap-2 lg:grid-cols-2">
            {CONTENT_TOGGLES.map((toggle) => (
              <label
                key={toggle.key}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/70 bg-surface2/40 px-3.5 py-2.5"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-[var(--brand,#5b4bdb)]"
                  checked={toggles[toggle.key]}
                  onChange={() =>
                    setToggles((current) => ({ ...current, [toggle.key]: !current[toggle.key] }))
                  }
                />
                <span>
                  <span className="block text-[13px] font-semibold text-text">{toggle.label}</span>
                  <span className="block text-[11.5px] text-text2">{toggle.detail}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                Minimum severity
              </span>
              <select
                className="input w-full py-2 text-[13px]"
                value={severityThreshold}
                onChange={(event) => setSeverityThreshold(event.target.value)}
              >
                <option value="info">Info and above (everything)</option>
                <option value="low">Low and above</option>
                <option value="medium">Medium and above</option>
                <option value="high">High and above</option>
                <option value="critical">Critical only</option>
              </select>
            </label>
            <div>
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                Finding statuses
              </span>
              <div className="flex flex-wrap gap-3 pt-1.5">
                {["open", "fixed", "suppressed"].map((status) => (
                  <label key={status} className="flex cursor-pointer items-center gap-1.5 text-[13px] text-text2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--brand,#5b4bdb)]"
                      checked={statuses.includes(status)}
                      onChange={() => toggleStatus(status)}
                    />
                    {status}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                Confidentiality label
              </span>
              <input
                className="input w-full py-2 text-[13px]"
                placeholder="e.g. Confidential"
                maxLength={120}
                value={classification}
                onChange={(event) => setClassification(event.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                Client / project name
              </span>
              <input
                className="input w-full py-2 text-[13px]"
                placeholder="Optional"
                maxLength={200}
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
                Prepared for
              </span>
              <input
                className="input w-full py-2 text-[13px]"
                placeholder="Optional"
                maxLength={200}
                value={preparedFor}
                onChange={(event) => setPreparedFor(event.target.value)}
              />
            </label>
          </div>
          <p className="text-[11.5px] leading-5 text-muted">
            These values are printed as plain text on the cover page. Formatting or markup is not
            interpreted.
          </p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {(catalog?.formats || []).map((format) => {
              const sbomFormat = format.id === "cyclonedx" || format.id === "spdx";
              const disabled = sbomFormat && !sbomAvailable;
              return (
                <label
                  key={format.id}
                  className={
                    disabled
                      ? "flex cursor-not-allowed items-start gap-3 rounded-lg border border-border/50 bg-surface2/20 px-3.5 py-2.5 opacity-60"
                      : "flex cursor-pointer items-start gap-3 rounded-lg border border-border/70 bg-surface2/40 px-3.5 py-2.5"
                  }
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[var(--brand,#5b4bdb)]"
                    disabled={disabled}
                    checked={formats.includes(format.id)}
                    onChange={() => toggleFormat(format.id)}
                  />
                  <span>
                    <span className="block text-[13px] font-semibold text-text">{format.label}</span>
                    <span className="block text-[11px] text-muted">{format.contentType}</span>
                    {disabled && (
                      <span className="mt-0.5 block text-[11px] text-warning">
                        No persisted SBOM exists for this repository.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="text-[11.5px] leading-5 text-muted">
            The PDF is rendered server-side with a deterministic layout engine. The HTML document
            is always stored alongside your selection for in-browser preview.
          </p>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-border/50">
              <tr>
                <td className="w-44 py-2 font-semibold text-muted">Report type</td>
                <td className="py-2 text-text">{selectedType?.title || reportType}</td>
              </tr>
              <tr>
                <td className="py-2 font-semibold text-muted">Scope</td>
                <td className="py-2 text-text2">
                  {repository?.name} · scan {scan?.scanId ? String(scan.scanId).slice(0, 8) : "—"} ·
                  commit {commit ? commit.slice(0, 10) : "—"}
                </td>
              </tr>
              <tr>
                <td className="py-2 font-semibold text-muted">Sections</td>
                <td className="py-2 text-text2">
                  {CONTENT_TOGGLES.filter((toggle) => toggles[toggle.key])
                    .map((toggle) => toggle.label)
                    .join(", ") || "None"}
                </td>
              </tr>
              <tr>
                <td className="py-2 font-semibold text-muted">Findings</td>
                <td className="py-2 text-text2">
                  {severityThreshold === "info" ? "All severities" : `${severityThreshold} and above`} ·
                  statuses: {statuses.join(", ")}
                </td>
              </tr>
              <tr>
                <td className="py-2 font-semibold text-muted">Formats</td>
                <td className="py-2 text-text2">
                  {formats.map((format) => FORMAT_LABELS[format] || format).join(", ")}
                </td>
              </tr>
              {(classification || clientName || preparedFor) && (
                <tr>
                  <td className="py-2 font-semibold text-muted">Cover page</td>
                  <td className="py-2 text-text2">
                    {[classification, clientName, preparedFor].filter(Boolean).join(" · ")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <CaveatNote tone="brand">
            The report will state its evidence boundaries, including: evidence mapping supports
            control review — it is not certification, an audit opinion, or proof of operating
            effectiveness; automated license classification is not legal advice.
          </CaveatNote>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-3">
          <p className="text-[13px] leading-6 text-text2">
            Submitting creates a durable report job. The evidence snapshot is captured immediately;
            rendering and PDF generation run on the server and typically finish within a minute.
            You can leave this page — the report stays in the history below.
          </p>
          {error && <CaveatNote tone="critical">{error}</CaveatNote>}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between border-t border-border/50 pt-4">
        <button
          type="button"
          className="btn-ghost px-3 py-2 text-xs"
          onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
          disabled={submitting}
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {step === 0 ? "Cancel" : "Back"}
        </button>
        {step < WIZARD_STEPS.length - 1 ? (
          <button
            type="button"
            className="btn-primary px-4 py-2 text-xs"
            onClick={() => setStep(step + 1)}
            disabled={!canContinue}
          >
            Continue
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary px-4 py-2 text-xs"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <FilePlus2 className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {submitting ? "Submitting…" : "Generate report"}
          </button>
        )}
      </div>
    </SectionPanel>
  );
}

// ---------------------------------------------------------------------------
// Run row
// ---------------------------------------------------------------------------

function RunCard({
  run,
  repoId,
  onChanged,
}: {
  run: ReportRun;
  repoId: string;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<string | undefined>();
  const [actionError, setActionError] = useState<string | undefined>();
  const active = run.status === "queued" || run.status === "generating";

  const act = async (name: string, work: () => Promise<unknown>) => {
    setBusy(name);
    setActionError(undefined);
    try {
      await work();
      onChanged();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : `${name} failed.`);
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <div className="asr-panel px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-brand/20">
          <FileText className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className="truncate text-left text-[13.5px] font-semibold text-text hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              aria-expanded={expanded}
            >
              {run.reportTitle}
            </button>
            <RunStatusPill run={run} />
            <Pill tone="brand" icon={<Lock className="h-3 w-3" aria-hidden="true" />}>
              Immutable
            </Pill>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-muted">
            <span>Requested {fmtDateTime(run.requestedAt) || "—"}</span>
            <span className="font-mono">Generated from scan {run.scanId.slice(0, 8)}</span>
            {/^[0-9a-f]{7,64}$/i.test(run.commit) && (
              <span className="inline-flex items-center gap-1 font-mono">
                <GitCommitHorizontal className="h-3 w-3" aria-hidden="true" />
                Measured from commit {run.commit.slice(0, 10)}
              </span>
            )}
            <span>{run.formats.map((format) => FORMAT_LABELS[format] || format).join(" · ")}</span>
          </div>
        </div>
        <div className="flex flex-none items-center gap-1.5">
          {run.status === "completed" && (
            <>
              <a
                href={reportPreviewHref(repoId, run.id)}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost px-2.5 py-1.5 text-xs"
              >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                Preview
              </a>
              {run.formats.includes("pdf") && (
                <a
                  href={reportDownloadHref(repoId, run.id, "pdf")}
                  className="btn-primary px-2.5 py-1.5 text-xs"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  PDF
                </a>
              )}
              <button
                type="button"
                className="btn-ghost px-2.5 py-1.5 text-xs"
                disabled={Boolean(busy)}
                title="Create a read-only link anyone can open — no login needed. Valid 30 days, revocable."
                onClick={() =>
                  act("share", async () => {
                    // Share the readable report only — never the raw evidence
                    // exports (JSON/CSV/SARIF) that were generated alongside it.
                    const readable = run.formats.filter((format) => format === "html" || format === "pdf");
                    const share = await shareReportRun(repoId, run.id, readable);
                    const shared = (share.formats || readable).join("/").toUpperCase();
                    try {
                      await navigator.clipboard.writeText(share.url);
                      window.alert(`Share link copied to clipboard.\n\nAnyone with the link can view the ${shared} report (read-only). It expires in 30 days and can be revoked.`);
                    } catch {
                      window.prompt("Copy this share link:", share.url);
                    }
                  })
                }
              >
                <Share2
                  className={busy === "share" ? "h-3.5 w-3.5 animate-pulse" : "h-3.5 w-3.5"}
                  aria-hidden="true"
                />
                Share
              </button>
            </>
          )}
          {(run.status === "completed" || run.status === "failed" || run.status === "expired") && (
            <button
              type="button"
              className="btn-ghost px-2.5 py-1.5 text-xs"
              disabled={Boolean(busy)}
              onClick={() => act("regenerate", () => regenerateReportRun(repoId, run.id))}
              title="Create a new run from the same frozen evidence"
            >
              <RefreshCw
                className={busy === "regenerate" ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"}
                aria-hidden="true"
              />
              Regenerate
            </button>
          )}
          {!active && (
            <button
              type="button"
              className="btn-ghost px-2.5 py-1.5 text-xs text-critical hover:bg-critical/10"
              disabled={Boolean(busy)}
              onClick={() => {
                if (window.confirm("Delete this report? Its stored artifacts will be removed.")) {
                  void act("delete", () => deleteReportRun(repoId, run.id));
                }
              }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {active && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11.5px] text-text2">
            <span>{run.progressStage || "Waiting for a generation slot"}</span>
            <span>{run.progressPercent != null ? `${run.progressPercent}%` : ""}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface2">
            <div
              className="h-full rounded-full bg-brand transition-all duration-500"
              style={{ width: `${Math.max(run.progressPercent ?? 4, 4)}%` }}
            />
          </div>
        </div>
      )}

      {run.status === "failed" && run.failureReason && (
        <div className="mt-3">
          <CaveatNote tone="critical">{run.failureReason}</CaveatNote>
        </div>
      )}
      {actionError && (
        <div className="mt-3">
          <CaveatNote tone="critical">{actionError}</CaveatNote>
        </div>
      )}

      {expanded && (
        <div className="mt-4 border-t border-border/50 pt-3.5">
          {run.artifacts && run.artifacts.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wide text-muted">
                    <th className="pb-2 pr-4 font-semibold">Artifact</th>
                    <th className="pb-2 pr-4 font-semibold">Size</th>
                    <th className="pb-2 pr-4 font-semibold">SHA-256</th>
                    <th className="pb-2 font-semibold" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {run.artifacts.map((artifact) => (
                    <tr key={artifact.name}>
                      <td className="py-2 pr-4">
                        <span className="block font-medium text-text">
                          {FORMAT_LABELS[artifact.format] || artifact.format}
                        </span>
                        <span className="block font-mono text-[10.5px] text-muted">
                          {artifact.filename}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-text2">{fmtBytes(artifact.bytes)}</td>
                      <td className="max-w-[220px] truncate py-2 pr-4 font-mono text-[10.5px] text-muted" title={artifact.sha256}>
                        {artifact.sha256}
                      </td>
                      <td className="py-2 text-right">
                        <a
                          href={reportDownloadHref(repoId, run.id, artifact.format)}
                          className="btn-ghost px-2 py-1 text-[11px]"
                        >
                          <Download className="h-3 w-3" aria-hidden="true" />
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : run.status === "completed" ? (
            <p className="text-[12px] text-muted">Loading artifact details…</p>
          ) : (
            <p className="text-[12px] text-muted">
              Artifacts appear here when generation completes.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted">
            <span>Requested by {run.requestedBy || "not_recorded"}</span>
            {run.completedAt && <span>Completed {fmtDateTime(run.completedAt)}</span>}
            {run.retentionUntil && <span>Retained until {fmtDateTime(run.retentionUntil)}</span>}
            {run.templateVersion && <span>Template {run.templateVersion}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace view
// ---------------------------------------------------------------------------

export function ReportWorkspaceView({
  assessment,
  repoId,
  scanState,
}: {
  assessment: AssuranceAssessment;
  repoId?: string;
  scanState: AssuranceScanState;
}) {
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const templatesQuery = useQuery({
    queryKey: ["assurance", "report-templates"],
    queryFn: fetchReportTemplates,
    staleTime: 10 * 60 * 1000,
  });

  const listQueryKey = ["assurance", "report-runs", repoId, search, statusFilter, typeFilter];
  const runsQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      fetchReportRuns(repoId as string, {
        q: search || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        limit: 50,
      }),
    enabled: Boolean(repoId),
    refetchInterval: (runsQueryData: unknown) => {
      const data =
        runsQueryData && typeof runsQueryData === "object" && "state" in (runsQueryData as object)
          ? (runsQueryData as { state: { data?: { reports?: ReportRun[] } } }).state.data
          : (runsQueryData as { reports?: ReportRun[] } | undefined);
      const hasActive = (data?.reports || []).some(
        (run) => run.status === "queued" || run.status === "generating"
      );
      return hasActive ? 2500 : false;
    },
  });

  const runs = useMemo(() => runsQuery.data?.reports || [], [runsQuery.data]);
  const completedRuns = runs.filter((run) => run.status === "completed");
  const activeRuns = runs.filter((run) => run.status === "queued" || run.status === "generating");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["assurance", "report-runs", repoId] });
  };

  if (!repoId) {
    return (
      <EmptyPanel
        title="No repository selected"
        detail="Select a repository to manage its Assurance reports."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile
          label="Reports"
          value={runsQuery.data ? fmtNumber(runsQuery.data.total) : undefined}
          sub="for this repository"
        />
        <MetricTile
          label="Completed"
          value={runsQuery.data ? fmtNumber(completedRuns.length) : undefined}
        />
        <MetricTile
          label="In progress"
          value={runsQuery.data ? fmtNumber(activeRuns.length) : undefined}
        />
        <MetricTile
          label="Latest report"
          value={completedRuns.length ? fmtDateTime(completedRuns[0].completedAt) : undefined}
        />
      </div>

      {!wizardOpen && (
        <SectionPanel
          title="Assurance reports"
          description="Point-in-time, immutable documents generated from persisted scanner evidence"
          actions={
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              disabled={scanState !== "completed"}
              className="btn-primary px-3.5 py-2 text-xs"
            >
              <FilePlus2 className="h-3.5 w-3.5" aria-hidden="true" />
              Generate report
            </button>
          }
        >
          <p className="text-[13px] leading-6 text-text2">
            Each report captures an evidence snapshot at request time and renders it into the
            formats you choose — including a server-generated PDF. Every artifact carries a
            SHA-256 integrity digest, and a stored report never changes after generation.
          </p>
          {scanState === "failed" && (
            <div className="mt-3">
              <CaveatNote tone="critical">
                The latest scan failed. Run a new scan before generating Assurance reports.
              </CaveatNote>
            </div>
          )}
          {scanState === "running" && (
            <div className="mt-3">
              <CaveatNote>A scan is still running. Generate reports when it completes.</CaveatNote>
            </div>
          )}
          {scanState === "none" && (
            <div className="mt-3">
              <CaveatNote>
                No completed assessment is available for this repository. Start a scan to generate
                Assurance evidence.
              </CaveatNote>
            </div>
          )}
        </SectionPanel>
      )}

      {wizardOpen && (
        <ReportWizard
          repoId={repoId}
          assessment={assessment}
          onClose={() => setWizardOpen(false)}
          onCreated={() => {
            setWizardOpen(false);
            setStatusFilter("");
            setTypeFilter("");
            setSearch("");
            refresh();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="input w-64 py-2 text-[13px]"
          placeholder="Search reports…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search reports"
        />
        <select
          className="input w-44 py-2 text-[13px]"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="generating">Generating</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
        </select>
        <select
          className="input w-64 py-2 text-[13px]"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          aria-label="Filter by report type"
        >
          <option value="">All report types</option>
          {(templatesQuery.data?.reportTypes || []).map((type) => (
            <option key={type.id} value={type.id}>
              {type.title}
            </option>
          ))}
        </select>
      </div>

      {runsQuery.isLoading ? (
        <div className="asr-panel p-6">
          <div className="skeleton h-5 w-56 rounded" />
          <div className="mt-4 space-y-2">
            <div className="skeleton h-16 rounded-md" />
            <div className="skeleton h-16 rounded-md" />
          </div>
        </div>
      ) : runs.length ? (
        <div className="space-y-2.5">
          {runs.map((run) => (
            <RunDetailLoader key={run.id} run={run} repoId={repoId} onChanged={refresh} />
          ))}
        </div>
      ) : (
        <EmptyPanel
          title={
            search || statusFilter || typeFilter
              ? "No reports match the current filters"
              : "No reports have been generated yet"
          }
          detail={
            search || statusFilter || typeFilter
              ? "Adjust or clear the filters to see other reports."
              : scanState === "completed"
                ? "Generate a report to produce an immutable, downloadable assessment document from the latest completed scan."
                : "Reports can be generated once a scan completes for this repository."
          }
        />
      )}
    </div>
  );
}

/**
 * Completed runs need their artifact list (hashes, sizes) which the list
 * endpoint intentionally omits; fetch the detail lazily per card.
 */
function RunDetailLoader({
  run,
  repoId,
  onChanged,
}: {
  run: ReportRun;
  repoId: string;
  onChanged: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ["assurance", "report-run", repoId, run.id, run.status],
    queryFn: async () => {
      const { fetchReportRun } = await import("../../../assurance/data");
      return fetchReportRun(repoId, run.id);
    },
    enabled: run.status === "completed",
    staleTime: 60 * 60 * 1000,
  });
  const merged = detailQuery.data ? { ...run, artifacts: detailQuery.data.artifacts } : run;
  return <RunCard run={merged} repoId={repoId} onChanged={onChanged} />;
}
